import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  BackLink,
  Button,
  FailureReasonBadge,
  GuardrailPill,
  Icon,
  Input,
  LastUpdated,
  Modal,
  NavRail,
  PageHeader,
  StatusBadge,
  Toast,
  Textarea,
} from "@ardept/design-system";
import { useJob, useJobEvents, useMe } from "../api/queries.js";
import type { ApprovalGateFieldView } from "../api/types.js";
import {
  buildSubmission,
  isBlocked,
  isVisible,
  seedAnswers,
  type DecisionAnswers,
} from "../api/decisionFields.js";
import { readServerRefusal } from "../api/serverRefusal.js";
import { usePostDecision } from "../api/usePostDecision.js";
import { usePostOutstandingItemDecision } from "../api/usePostOutstandingItemDecision.js";
import { EventTimeline } from "./EventTimeline.js";
import { OutstandingItemsPanel } from "./OutstandingItemsPanel.js";
import { jobBadgeStatus, ENGINE_STATE_PENDING_REVIEW } from "./_status.js";
import "./JobDetailScreen.css";
import "./screens.css";

/** Phase 550 wave 3 task 3.1 — the "this gate declares nothing" array, hoisted so its identity is
 *  stable across renders (a fresh `[]` inside the component would re-run the seeding effect on
 *  every poll and erase what the operator is typing). */
const NO_DECISION_FIELDS: readonly ApprovalGateFieldView[] = [];

// CON-04: 4 canonical guardrail names (LOCKED)
const CANONICAL_GUARDRAILS = [
  "no-pii-leak",
  "tone-professional",
  "claims-cited",
  "scope-in-framework",
] as const;

/**
 * Phase 532 wave 10 task 10.2 — the inline refusal band inside the approve /
 * reject dialogs.
 *
 * <p>The engine's `ErrorResponse.message` is written for the operator standing
 * in front of this modal: a gate that declares decision fields refuses
 * `decision_fields_required` and says, in words, which answers are owed and
 * where to give them. So the server's sentence is rendered VERBATIM, with its
 * closed-vocabulary `error_code` beneath it — a code an operator can quote into
 * a support thread and that an engineer can grep for.</p>
 *
 * <p>The two generic sentences remain, and only for what they were ever true
 * of: a transport failure or a 5xx, where there is no engine sentence to show.
 * Rendering the gate's DECLARED fields in this dialog is #1999, not this.</p>
 */
function DecisionErrorBand({ error }: { error: unknown }): JSX.Element {
  const refusal = readServerRefusal(error);
  if (refusal) {
    return (
      <div className="error-band">
        {refusal.message}
        <div className="mono muted" style={{ marginTop: 6 }}>
          {refusal.code}
        </div>
      </div>
    );
  }

  const status = (error as { status?: number } | null)?.status;
  return (
    <div className="error-band">
      {status === undefined || status >= 500
        ? "Couldn't reach the server. Try again."
        : "Could not record this decision. Check the job status and try again."}
    </div>
  );
}

/**
 * Phase 550 wave 3 task 3.1 (card #1999) — the gate's DECLARED fields, rendered inside the
 * decision dialog the operator is already standing in.
 *
 * <p>ONE OWNER PER RULE (AC-3). This component is JSX and nothing else. Which fields exist,
 * their order, their labels, their options, whether each is required, what the file computed,
 * the chip that says where that value came from, the sentence that says why there is no value,
 * and which sibling reveals a reason field — every one of those was resolved SERVER-side by the
 * reader the staff approval form and the submit-time validator already share
 * (`ApprovalGateDecisionProjection`). Nothing below re-derives any of it, and nothing below
 * holds a band, option, label or role literal: the declaration order is the server's array
 * order, and the reveal predicate is read through `isVisible` in
 * `../api/decisionFields.js` — the DOM-free module this app can actually unit-test.</p>
 *
 * <p>NO CLIENT-SIDE AUTHORITY. There is no override role here, rendered or branched on. A
 * declared `override_role` is enforced at SUBMIT by the engine, whose refusal arrives verbatim
 * through the untouched `DecisionErrorBand` above. A console that pre-judged authority would be
 * a second owner of the one question the decisions door already answers.</p>
 *
 * <p>CONTROLS. `@ardept/design-system` exports `Button`, `Modal`, `Input`, `Textarea` and
 * `MultiSelect` — and no single `Select` (packages/design-system/src/index.ts). So a `select`
 * field renders as a native `<select>` styled from `--ard-*` tokens in JobDetailScreen.css;
 * minting a kit component is a design-system change on a different card. `type` is an OPEN
 * vocabulary (types.ts), so anything this file does not know renders as a plain text control
 * rather than as an error — a gate declaring a new control kind stays answerable here.</p>
 *
 * <p>Renders NOTHING — no wrapper, no spacing — when the gate declares no fields or when every
 * declared field is hidden by its predicate, which is what keeps a dialog for a gate that
 * declares nothing byte-identical to the one that shipped before this phase (AC-2).</p>
 */
function DecisionFields({
  idPrefix,
  fields,
  answers,
  onAnswer,
}: {
  idPrefix: string;
  fields: readonly ApprovalGateFieldView[];
  answers: DecisionAnswers;
  onAnswer: (name: string, value: string) => void;
}): JSX.Element | null {
  const visible = fields.filter((field) => isVisible(field, answers));
  if (visible.length === 0) return null;

  return (
    <div className="decision-fields">
      {visible.map((field) => {
        const controlId = `${idPrefix}-decision-field-${field.name}`;
        const value = answers[field.name] ?? "";
        return (
          <div className="decision-field" key={field.name}>
            <label htmlFor={controlId}>
              {field.label}
              {field.required && <span className="req">*</span>}
            </label>
            {field.type === "select" ? (
              <select
                id={controlId}
                className="decision-field__control decision-field__select"
                value={value}
                required={field.required}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  onAnswer(field.name, e.target.value)
                }
              >
                {/* The unanswered state, spelled as a blank row rather than as copy: the
                    console has no sentence to put here that the workflow did not declare,
                    and a <select> whose value is "" with no matching <option> would show
                    the first option while the state says nothing was chosen. */}
                <option value="" />
                {(field.options ?? []).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : field.type === "textarea" ? (
              <Textarea
                id={controlId}
                className="decision-field__control"
                value={value}
                required={field.required}
                rows={3}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  onAnswer(field.name, e.target.value)
                }
              />
            ) : (
              <Input
                id={controlId}
                className="decision-field__control"
                value={value}
                required={field.required}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onAnswer(field.name, e.target.value)
                }
              />
            )}
            {/* Provenance, in the ENGINE's words. `unresolved_reason` REPLACES the chip: when
                the file cannot answer the field's `computed_from` yet, the operator is looking
                at an empty required control and is owed the reason — the same sentence the
                staff form shows (FormCtxPreAnswerResolver.ReasonNoCandidate) rather than a
                value nobody computed. Inventing a sentence of our own here would be a second
                vocabulary for one fact. */}
            {field.unresolved_reason !== undefined ? (
              <span className="decision-field__note decision-field__unresolved">
                {field.unresolved_reason}
              </span>
            ) : field.source_label !== undefined ? (
              <span className="decision-field__note decision-field__source">
                {field.source_label}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/** Helper: format ISO datetime as "DD Mon YYYY, HH:MM UTC" */
function formatDateTimeUTC(iso: string): string {
  const d = new Date(iso);
  return (
    d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }) +
    " " +
    d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
    }) +
    " UTC"
  );
}

/** Phase 19 D-FAIL-04: format ISO date string as relative timestamp "Ns ago", "Nm ago", etc.
 *  Used by the Failure card "Failed:" row. */
function formatRelativeTime(iso: string): string {
  const now = Date.now();
  const ms = new Date(iso).getTime();
  const diff = Math.max(0, Math.floor((now - ms) / 1000));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const d = new Date(ms);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export function JobDetailScreen(): JSX.Element {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();

  const {
    data: job,
    isLoading,
    error,
    dataUpdatedAt,
  } = useJob(jobId!);
  const { data: eventsPage } = useJobEvents(jobId!);
  const { data: me } = useMe();
  const events = eventsPage?.events ?? [];

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [approveNote, setApproveNote] = useState("");
  const [rejectNote, setRejectNote] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  /* ── Phase 550 wave 3 task 3.1 — the gate this job is parked at, and the approver's answers
     to what it DECLARES. The gate rides the job object the screen already polls (queries.ts
     `useJob`, POLL_OPTIONS), so there is no second fetch and no new route: `approval_gate` is
     simply absent on a job parked at no gate, or at a gate that declares nothing — and absence
     leaves every line below inert (`isBlocked([], {})` is false, `DecisionFields` renders null,
     and the mutate bodies fall through to the exact request they posted before this phase). */
  const gate = job?.approval_gate;
  const gateFields: readonly ApprovalGateFieldView[] = gate?.decision_fields ?? NO_DECISION_FIELDS;
  const gateDeclaresFields = gate !== undefined && gateFields.length > 0;
  const [answers, setAnswers] = useState<DecisionAnswers>({});
  const anyDecisionModalOpen = approveOpen || rejectOpen;

  /* Seeded when a dialog OPENS, and re-seeded when the POLLED gate itself changes underneath an
     open dialog — a gate that now declares a different question must not be answered with the
     answers to the old one. The dependency is the gate's CONTENT (serialised), not its object
     identity: this job re-renders every 4s, and a re-seed on each poll would erase what the
     operator is typing. The open handlers seed synchronously too, so the first painted frame
     already carries the computed values rather than flashing empty. */
  const gateSignature = gate === undefined ? "" : JSON.stringify(gate);
  useEffect(() => {
    if (!anyDecisionModalOpen) return;
    setAnswers(seedAnswers(gateFields));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gateSignature IS gateFields' content.
  }, [anyDecisionModalOpen, gateSignature]);

  // D-DEC-FLOW-01: wait-for-server then refetch; no optimistic update
  const decision = usePostDecision(jobId!);

  // Phase 544 wave 4 task 4.1 — recording an OWED ITEM ("received" / "not required"), which is a
  // different act on a different row from the approval above and therefore its own mutation with
  // its own pending and error state. Owned HERE rather than inside the panel so the panel stays a
  // pure function of its props and its sibling test can render the real tree in plain node.
  const itemDecision = usePostOutstandingItemDecision(jobId!);

  /* Phase 550 wave 3 task 3.1 — the two bodies, written out at BOTH call sites rather than
     behind a shared builder, because the difference between them is the contract this phase is
     judged on: a gate that declares fields is named (`gate`) and answered (`decision_fields`,
     built by the one module that owns what "answered" means); a job with no declaring gate posts
     the EXACT body it posted before — no `gate` key, no `decision_fields` key (AC-2).
     `usePostDecision` serialises the request whole, so neither case needs an edit there. */
  function submitApprove() {
    const rationale = approveNote.trim() || undefined;
    decision.mutate(
      gateDeclaresFields
        ? {
            decision: "approve",
            rationale,
            gate: gate.id,
            decision_fields: buildSubmission(gateFields, answers),
          }
        : { decision: "approve", rationale },
      {
        onSuccess: () => {
          setApproveOpen(false);
          setApproveNote("");
          setAnswers({});
          setToast("Decision recorded");
          setTimeout(() => setToast(null), 4000);
        },
      },
    );
  }

  function submitReject() {
    if (!rejectNote.trim()) return;
    const rationale = rejectNote.trim();
    decision.mutate(
      gateDeclaresFields
        ? {
            decision: "reject",
            rationale,
            gate: gate.id,
            decision_fields: buildSubmission(gateFields, answers),
          }
        : { decision: "reject", rationale },
      {
        onSuccess: () => {
          setRejectOpen(false);
          setRejectNote("");
          setAnswers({});
          setToast("Decision recorded");
          setTimeout(() => setToast(null), 4000);
        },
      },
    );
  }

  async function copyJobId() {
    if (!job) return;
    try {
      await navigator.clipboard.writeText(job.id);
    } catch {
      // Clipboard write failed; ignore silently
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  }

  /* Phase 550 wave 3 task 3.1 — opening seeds the declared answers with what the FILE computed
     (or "" where it computed nothing), so the operator is looking at the file's answer rather
     than at an empty form; closing clears them beside the existing note reset, so a dialog
     re-opened later cannot carry a previous sitting's answer into a new decision. */
  function openApproveModal() {
    setAnswers(seedAnswers(gateFields));
    setApproveOpen(true);
  }

  function openRejectModal() {
    setAnswers(seedAnswers(gateFields));
    setRejectOpen(true);
  }

  function closeApproveModal() {
    setApproveOpen(false);
    setApproveNote("");
    setAnswers({});
  }

  function closeRejectModal() {
    setRejectOpen(false);
    setRejectNote("");
    setAnswers({});
  }

  function answerDecisionField(name: string, value: string) {
    setAnswers((previous) => ({ ...previous, [name]: value }));
  }

  // Build operator display for NavRail from /me response
  const operatorDisplay = {
    name: me?.email
      ? (me.email.split("@")[0] ?? me.email)
          .replace(/[._-]/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase())
      : "Operator",
    email: me?.email ?? "",
  };

  if (isLoading && !job) {
    return (
      <div className="app">
        <NavRail active="jobs" operator={operatorDisplay} onNavigate={navigate} />
        <main className="main">
          <div className="detail-loading">Loading job...</div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app">
        <NavRail active="jobs" operator={operatorDisplay} onNavigate={navigate} />
        <main className="main">
          <BackLink to="/jobs" navigate={navigate} label="Back to jobs" />
          <div className="detail-error">
            Job not found, or you do not have access. Check the URL or your tenant scope.
          </div>
        </main>
      </div>
    );
  }

  if (!job) return <></>;

  const tenant = me?.allowed_tenants.find((t) => t.id === job.tenant_id);

  // B-1 fix: gate MUST use engine state literal "PENDING_REVIEW", NOT "pending_approval"
  const isPending = job.state === ENGINE_STATE_PENDING_REVIEW;

  // Phase 499: the badge reads the engine's derived `status` (+ the fail_reason arm),
  // NOT the workflow author's DSL `state` literal.
  const uiStatus = jobBadgeStatus(job);

  const contentHashShort = job.workflow_version_content_hash.slice(0, 12);

  return (
    <div className="app">
      <NavRail active="jobs" operator={operatorDisplay} onNavigate={navigate} />
      <main className="main">
        <BackLink to="/jobs" navigate={navigate} label="Back to jobs" />
        <PageHeader
          title={<span>Job <span className="main__title-id">{job.id.slice(0, 8)}</span></span>}
        />
        {dataUpdatedAt && <LastUpdated timestamp={dataUpdatedAt} />}

        {/* Phase 19 D-FAIL-04: Failure prominence card — only when the job has failed.
            Field names verified against platform/workflow-engine/Http/InternalApiDtos.cs.
            OperatorJobDto post-Plan 06 has: id, tenant_id, workflow_id,
            workflow_version_content_hash, state, started_at, last_event_at,
            aggregate_spend_usd, latest_guardrail_verdicts, fail_reason, status.
            NB (restated for Phase 499): `state` is the workflow author's DSL literal and
            `status` is the engine's DERIVED enum — neither is the failure signal. The
            canonical "is this job failed?" signal is, and remains, `fail_reason !== null`
            (it is also arm 1 of jobBadgeStatus, so banner and badge cannot disagree).
            Revision WARNING #6 fix: gate on `fail_reason !== null`, NOT `status === "failed"`. */}
        {job.fail_reason !== null && (
          <section className="failure-card" role="region" aria-label="Failure summary">
            <div className="failure-card__head">
              <Icon name="octagon-alert" size={20} aria-hidden="true" />
              <h3>This job failed</h3>
            </div>
            <div className="failure-card__body">
              <div className="failure-card__row">
                <label>Reason:</label>
                <FailureReasonBadge reason={job.fail_reason} />
              </div>
              <div className="failure-card__row">
                <label>State at failure:</label>
                <span className="ard-mono">{job.state}</span>
              </div>
              <div className="failure-card__row">
                <label>Failed:</label>
                <span title={job.last_event_at}>{formatRelativeTime(job.last_event_at)}</span>
              </div>
            </div>
            <a
              className="failure-card__back"
              href={`/failures?job=${job.id}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/failures?job=${job.id}`);
              }}
            >
              &larr; Back to Failures
            </a>
          </section>
        )}

        {/* Header summary card */}
        <section className="summary">
          <div className="summary__top">
            <div className="summary__title">
              <div className="summary__id-row">
                <span className="summary__id">{job.id}</span>
                <button
                  className="copy-btn"
                  aria-label="Copy job ID"
                  onClick={copyJobId}
                  title={copied ? "Copied" : "Copy"}
                >
                  <Icon name={copied ? "check" : "copy"} size={14} />
                </button>
              </div>
              <h2 className="ard-h2 summary__h2">{job.workflow_id}</h2>
              <div className="summary__tenant">
                <span>{tenant?.display_name ?? "Unknown tenant"}</span>
                <span className="slug">{tenant?.slug ?? "?"}</span>
              </div>
              <div className="summary__wf">
                <span className="wf">{job.workflow_id}</span>
                <span className="hash">{contentHashShort}...</span>
              </div>
            </div>
            <div className="summary__meta">
              <div className="meta-cell">
                <label>Current state</label>
                <span className="v mono">{job.state}</span>
              </div>
              <div className="meta-cell">
                <label>Started</label>
                <span className="v">{formatDateTimeUTC(job.started_at)}</span>
              </div>
              <div className="meta-cell">
                <label>Last event</label>
                <span className="v">{formatDateTimeUTC(job.last_event_at)}</span>
              </div>
              <div className="meta-cell">
                <label>Aggregate spend</label>
                <span className="v mono">${job.aggregate_spend_usd.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="summary__actions">
            <div className="summary__status-line">
              {/* B-1 fix: StatusBadge consumes uiStatus (translated label), NOT raw job.state */}
              <StatusBadge status={uiStatus} />
              {/* Phase 526 wave 4 task 4.1 (AC-5) — what the client may still send, beside the
                  badge. Renders NOTHING when the field is absent (this workflow declares no
                  supply-later item) or when count is 0 (declared, nothing owed): an empty note is
                  worse than no note. `labels_text` is read out VERBATIM — the engine binder
                  pre-renders it so the console and the staff agent cannot word it differently, and
                  the words themselves are the workflow author's, not the platform's (D4). */}
              {job.supply_later && job.supply_later.count > 0 && (
                <span className="summary__outstanding">
                  Outstanding: {job.supply_later.labels_text}
                </span>
              )}
            </div>
            {/* B-1 fix: Approve/Reject gate uses ENGINE_STATE_PENDING_REVIEW literal ("PENDING_REVIEW") */}
            {isPending && (
              <div className="summary__btns">
                <Button
                  variant="ghost"
                  size="lg"
                  onClick={openRejectModal}
                >
                  Reject job
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={openApproveModal}
                >
                  Approve job
                </Button>
              </div>
            )}
          </div>

          {/* Guardrail row: CON-04: 4 canonical pills always visible (muted when no verdicts yet) */}
          <div className="guard-row">
            {CANONICAL_GUARDRAILS.map((name) => (
              <GuardrailPill
                key={name}
                name={name}
                verdict={job.latest_guardrail_verdicts[name]}
              />
            ))}
          </div>
        </section>

        {/* Two-column layout: timeline (left) + agent state panel (right) */}
        <div className="detail-grid">
          {/* LEFT: Event timeline */}
          <section className="timeline">
            <div className="panel-head">
              <span>Event timeline</span>
              <span className="meta">{events.length} events, oldest first</span>
            </div>
            {/* EventTimeline rendered OLDEST AT TOP (kit README) */}
            <EventTimeline events={events} />
          </section>

          {/* RIGHT: Agent state panel */}
          <aside className="agent-panel">
            <div className="panel-head">
              <span>Agent state</span>
              <span className="meta">{contentHashShort}</span>
            </div>
            <div className="agent-panel__top">
              <div>
                <div
                  style={{
                    font: "var(--caption-weight) var(--caption-size)/1 var(--font-ui)",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: "var(--ard-muted)",
                    marginBottom: 4,
                  }}
                >
                  Current state
                </div>
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 18,
                    letterSpacing: "0.02em",
                    color: "var(--ard-ink-text)",
                    fontWeight: 500,
                  }}
                >
                  {job.state}
                </span>
              </div>
            </div>
            <div className="kv-grid">
              <div className="kv">
                <label>Workflow</label>
                <span className="v mono">{job.workflow_id}</span>
              </div>
              <div className="kv">
                <label>Content hash</label>
                <span className="v mono">{contentHashShort}...</span>
              </div>
              <div className="kv">
                <label>Tenant</label>
                <span className="v">{tenant?.display_name ?? "Unknown"}</span>
              </div>
              <div className="kv">
                <label>Started</label>
                <span className="v mono">{formatDateTimeUTC(job.started_at)}</span>
              </div>
              <div className="kv">
                <label>Last event</label>
                <span className="v mono">{formatDateTimeUTC(job.last_event_at)}</span>
              </div>
              <div className="kv">
                <label>Spend</label>
                <span className="v mono">${job.aggregate_spend_usd.toFixed(2)}</span>
              </div>
            </div>
            {/* Phase 529 wave 6 task 6.2 (AC-6) — what this file still owes, from the PERSISTED
                outstanding-items ledger, through the SAME read model the staff agent is
                grounded on and the SAME two counts the engine's pre-push precondition enforces.
                Renders NOTHING when the `outstanding` key is absent, which is what a route that
                does not carry the answer looks like (the jobs LIST); absence is never read as
                "clear". Every word it renders is either the engine's own closed vocabulary or
                the workflow author's declared label — the console invents no sentence about
                what a client owes. */}
            {/* Phase 544 wave 4 task 4.1 — …and RECORDS one: "Not required" on every open item,
                "Record as received" only where the declaration says a staff act settles it. The
                panel decides neither — it reads `settled_by` off the row and posts the OUTCOME to
                the one engine route, which maps it onto `close_outstanding_item` /
                `waive_outstanding_item`. */}
            <OutstandingItemsPanel
              outstanding={job.outstanding}
              items={job.outstanding_items}
              onDecide={(request) => itemDecision.mutate(request)}
              decisionPending={itemDecision.isPending}
              decisionError={itemDecision.error}
            />
          </aside>
        </div>

        {/* Approve modal (D-DEC-FLOW-01: wait-for-server, spinner label, inline error) */}
        <Modal
          open={approveOpen}
          onClose={closeApproveModal}
          title="Approve this job?"
          footer={
            <>
              <Button
                variant="ghost"
                onClick={closeApproveModal}
                disabled={decision.isPending}
              >
                Cancel
              </Button>
              {/* Phase 550 wave 3 task 3.1 — the browser asks for what the gate DECLARES
                  instead of letting the operator meet the obligation at a 400. `isBlocked` is
                  false by construction for a gate declaring nothing, so this predicate is the
                  one that shipped before, unchanged, for every such job. */}
              <Button
                variant="primary"
                onClick={submitApprove}
                disabled={decision.isPending || isBlocked(gateFields, answers)}
              >
                {decision.isPending && approveOpen
                  ? "Recording decision..."
                  : "Approve job"}
              </Button>
            </>
          }
        >
          <p className="modal__sub" style={{ marginBottom: 14 }}>
            The agent run will be marked complete and follow-on actions will execute.
          </p>
          <DecisionFields
            idPrefix="approve"
            fields={gateFields}
            answers={answers}
            onAnswer={answerDecisionField}
          />
          <label>Rationale (optional)</label>
          <Textarea
            value={approveNote}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setApproveNote(e.target.value)}
            placeholder="Leave a note for the audit trail."
            rows={4}
          />
          {decision.isError && approveOpen && (
            <DecisionErrorBand error={decision.error} />
          )}
        </Modal>

        {/* Reject modal (D-DEC-FLOW-01: rationale required, destructive button) */}
        <Modal
          open={rejectOpen}
          onClose={closeRejectModal}
          title="Reject this job?"
          footer={
            <>
              <Button
                variant="ghost"
                onClick={closeRejectModal}
                disabled={decision.isPending}
              >
                Cancel
              </Button>
              {/* Kit non-negotiable: variant="destructive" uses --status-rejected (#a82828), NOT coral */}
              {/* Phase 550 wave 3 task 3.1 — the same block, because engine requiredness is
                  TRIGGER-AGNOSTIC: the declared-field checks in `ValidateAsync` do not consult
                  `trigger`, and the gate-less refusal in the decisions handler fires for
                  `approval_rejected` too. The existing non-blank-rationale condition stands. */}
              <Button
                variant="destructive"
                onClick={submitReject}
                disabled={
                  !rejectNote.trim() || decision.isPending || isBlocked(gateFields, answers)
                }
              >
                {decision.isPending && rejectOpen
                  ? "Recording decision..."
                  : "Reject job"}
              </Button>
            </>
          }
        >
          <p className="modal__sub" style={{ marginBottom: 14 }}>
            The job will halt. The reason is written to the audit log and visible to the tenant.
          </p>
          <DecisionFields
            idPrefix="reject"
            fields={gateFields}
            answers={answers}
            onAnswer={answerDecisionField}
          />
          <label>
            Rationale <span className="req">*</span>
          </label>
          <Textarea
            value={rejectNote}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setRejectNote(e.target.value)}
            placeholder="Explain the rejection. The tenant sees this."
            rows={4}
            required
          />
          {decision.isError && rejectOpen && (
            <DecisionErrorBand error={decision.error} />
          )}
        </Modal>

        {/* Toast (D-DEC-FLOW-01 success feedback) */}
        {toast && (
          <Toast message={toast} onDismiss={() => setToast(null)} />
        )}
      </main>
    </div>
  );
}
